import Component from '@glimmer/component'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { task, didCancel } from 'ember-concurrency'
import { join } from 'teacher-ui-client/utils/uri'
import config from 'teacher-ui-client/config/environment'
import { tracked } from '@glimmer/tracking'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type Teacher from 'teacher-ui-client/models/teacher'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { FormComponentsFormSelect } from 'teacher-ui-client/components/form-components/form-select'
import { FormWrapperSubmitButton } from 'teacher-ui-client/components/form-wrapper/submit-button'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { t } from 'ember-intl'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { optionTemplateUserIconText } from 'teacher-ui-client/utils/dropdown-templates'
import type Owner from '@ember/owner'
import type { SelectOption } from 'teacher-ui-client/components/form-components/form-select/option'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface SchoolClassOption extends SelectOption {
  name: string
  id: string
}

function schoolClassAsOption(schoolClass: SchoolClass & { id: string }): SchoolClassOption {
  const text = schoolClass.name
  const uid = schoolClass.id
  const value = uid
  return {
    text,
    value,
    uid,
    name: text,
    id: uid,
  }
}

interface Args {
  teacher: Teacher
  teachers: Teacher[]
  subscriptionType: SubscriptionType
}

interface Signature {
  Element: HTMLFormElement
  Args: Args
}

export class JoinSchoolClass extends Component<Signature> {
  @service intl!: IntlService
  @service flashQueue!: FlashQueueService
  @service log!: Log
  @service authToken!: AuthToken

  @tracked selectedSchoolClassOption: SchoolClassOption | undefined

  constructor(owner: Owner, args: Args) {
    super(owner, args)
  }

  get schoolClass() {
    return this.selectedSchoolClassOption
  }

  @tracked
  requestedSchoolClassIds: string[] = []

  teacherAsOptionGroup = (teacher: Teacher & { id: string }) => {
    const schoolClasses = teacher.schoolClasses

    const requestingTeacherId = this.args.teacher?.id
    const { subscriptionType } = this.args
    const text = teacher.fullName
    const uid = teacher.id
    const filteredSchoolClasses = schoolClasses.filter(function (schoolClass) {
      if (schoolClass.subscriptionType !== subscriptionType) return false
      // @ts-expect-error - something wrong with relationship reference types
      const teachersRef = schoolClass.hasMany<Teacher, 'teachers'>('teachers')
      const teacherIds = teachersRef.ids()
      if (teacherIds.includes(requestingTeacherId)) return false
      return true
    })
    const value = filteredSchoolClasses.filter(hasId).map(schoolClassAsOption)
    return {
      text,
      value,
      uid,
    }
  }

  get schoolClassesAsOptions() {
    const optionGroups = this.args.teachers.filter(hasId).map((teacher) => this.teacherAsOptionGroup(teacher))
    const optionGroupsWithClasses = optionGroups.filter((group) => group.value.length)
    return optionGroupsWithClasses.sort((a, b) => a.text.localeCompare(b.text))
  }

  get defaultSubmitButtonText() {
    return this.intl.t('manage.schoolClasses.joinFormSubmit')
  }

  get submitButtonState() {
    const taskInstance = this.sendRequest?.last
    if (!taskInstance) return 'default'
    if (taskInstance.isRunning) {
      return 'load'
    }
    if (taskInstance.isSuccessful) {
      return 'done'
    }
    if (taskInstance.isError) {
      return 'error'
    }
    return 'default'
  }

  sendRequest = task(
    { restartable: true },
    async ({ teacher, schoolClass }: { teacher: Teacher; schoolClass: SchoolClassOption }) => {
      try {
        if (!hasId(teacher)) throw new Error('teacher for join class request is missing id')
        await this.joinSchoolClassRequest(teacher.id, schoolClass.id)
        const name = schoolClass.name
        const id = schoolClass.id
        const successMessage = this.intl.t('manage.schoolClasses.joinSuccess', { name })
        this.requestedSchoolClassIds.push(id)
        this.flashQueue.addSuccess({ subtitle: successMessage })
      } catch (error) {
        if (didCancel(error)) return
        this.log.error('failed to join school class', error ?? undefined)
        const errorMessage = this.intl.t('manage.schoolClasses.joinError')
        this.flashQueue.addFail({ subtitle: errorMessage })
      }
    },
  )

  isJoinSchoolClassRequested(schoolClass: SchoolClassOption) {
    const schoolClassId = schoolClass.id
    if (!this.requestedSchoolClassIds) return
    return this.requestedSchoolClassIds.includes(schoolClassId)
  }

  @action
  onSubmit(event: Event) {
    event.preventDefault()

    if (!this.schoolClass) return
    if (this.isJoinSchoolClassRequested(this.schoolClass)) {
      const name = this.schoolClass.name
      const translationKey = 'manage.schoolClasses.joinErrorDuplicateRequest'
      const duplicateRequestMessage = this.intl.t(translationKey, { name })
      this.flashQueue.addCaution({ subtitle: duplicateRequestMessage })
      return false
    }
    void this.sendRequest.perform({ teacher: this.args.teacher, schoolClass: this.schoolClass })
    return false
  }

  @action
  updateSchoolClass(schoolClass: SchoolClass | null) {
    if (!schoolClass || !hasId(schoolClass)) return
    this.selectedSchoolClassOption = schoolClassAsOption(schoolClass)
  }

  async joinSchoolClassRequest(_teacherId: string, schoolClassId: string) {
    const url = join(config.gravityV3Url, 'join-school-class-request', schoolClassId).toString()

    // Request doesn't need anything in the body as the id of the school class is in the url
    // and the teacher id is associated with the session
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({}),
      headers: {
        Authorization: this.authToken?.token ?? '',
        'content-type': 'application/vnd.api+json',
      },
    })
    if (!response.ok) throw new Error(await response.text())
  }

  <template>
    <form {{on "submit" this.onSubmit}} class="flex flex-row gap-2" ...attributes>
      <div class="min-w-40 shrink-0 grow basis-0">
        <FormComponentsFormSelect
          data-test-join-school-class-select
          @optionClick={{this.updateSchoolClass}}
          @optionTemplate={{optionTemplateUserIconText}}
          @resetAction={{fn this.updateSchoolClass null}}
          @defaultText={{t "manage.schoolClasses.selectSchoolClass"}}
          @value={{this.selectedSchoolClassOption}}
          @options={{this.schoolClassesAsOptions}}
          @secondLevelOnly={{true}}
        />
      </div>
      <FormWrapperSubmitButton
        data-test-join-school-class-submit
        @defaultText={{this.defaultSubmitButtonText}}
        @appearance={{ButtonAppearance.regular}}
        @state={{this.submitButtonState}}
        @disabled={{false}}
      />
    </form>
  </template>
}

export default JoinSchoolClass
